import { GenericOfficeIDs, SchedulingFilter } from '@caresend/types';
import { initFlowHelpers, addAssociatedIDs } from '../../../../flow/helpers.js';
import { toastError } from '../../../../functions/toast/toast.js';
import { getRoute } from '../../../../router/helpers/initRouter.js';
import { SchedulingFlowRouteName } from '../../../../router/routes/schedulingFlow/model.js';
import { isPatientUser } from '../../../helpers.js';
import { getStore } from '../../../helpers/initStore.js';
import { schedulingFlowCompletionActions } from './completionActions.js';
import { SchedulingFlowParam, SchedulingFlowRouteTitles } from './model.js';
import __vue_component__$1 from './secondaryHeaderComponents/DiscardChangesButton.vue.js';
import { schedulingFlowStepValidators } from './stepValidation.js';
import __vue_component__ from './titleHeaderComponents/VisitTypeTabs.vue.js';

// FLOW CONFIG
/** ID generated, all data fetched/initialized, any required navigation completed */
const getFlowLoading = () => {
    const store = getStore();
    const route = getRoute();
    const { draftWaypointID } = route.value.params;
    if (!draftWaypointID)
        return true;
    return !store.state.schedulingFlow.localState.flowLoadingCompleted[draftWaypointID];
};
/** All data fetched/initialized. */
const getFlowDataIsReady = (draftWaypointID) => {
    const store = getStore();
    const route = getRoute();
    // In some cases the draftWaypointID is not in the route yet, but we need
    // to check for data. In these cases, we can pass in the draftWaypointID
    // as a param.
    const id = draftWaypointID !== null && draftWaypointID !== void 0 ? draftWaypointID : route.value.params.draftWaypointID;
    if (!id)
        return false;
    return !!store.state.schedulingFlow.localState.flowDataIsReady[id];
};
const setNextLoading = (isLoading) => {
    const store = getStore();
    store.commit('schedulingFlow/SET_NEXT_BUTTON_LOADING', isLoading);
};
const flowConfig = {
    completionActions: schedulingFlowCompletionActions,
    getFlowDataIsReady,
    getFlowLoading,
    rootParam: SchedulingFlowParam.DRAFT_WAYPOINT_ID,
    setNextLoading,
    stepValidators: schedulingFlowStepValidators,
};
// STEP BUILDING HELPERS
const addTimeHeaderComponents = (step) => {
    const { schedulingFilters } = getStore().state.schedulingFlow;
    const siteCollectionEnabled = schedulingFilters === null || schedulingFilters === void 0 ? void 0 : schedulingFilters.includes(SchedulingFilter.SITE_COLLECTION);
    // In make sure to hide the Home/Lab collection tab
    // if the site collection is not enabled.
    const showTertieryComponent = siteCollectionEnabled;
    const forceNavigateToVisitPage = step.routeName === SchedulingFlowRouteName.SCHEDULING_FLOW_FACILITY_TIME;
    return {
        ...step,
        backHeaderComponent: {
            component: () => import('./backHeaderComponents/TimeBackButton.vue.js'),
            props: { fullWidthIfMobile: !showTertieryComponent, forceNavigateToVisitPage },
        },
        tertiaryHeaderComponent: showTertieryComponent ? {
            // Non-dynamic import of `VisitTypeTabs` used to support transition between tab settings
            component: __vue_component__,
            props: {},
        } : undefined,
    };
};
const addDiscardChangesSecondaryHeaderComponent = (step) => {
    var _a;
    const store = getStore();
    const oldDraftWaypointID = (_a = store.state.schedulingFlow.localState.oldDraftWaypoint) === null || _a === void 0 ? void 0 : _a.id;
    if (!oldDraftWaypointID)
        return step;
    return {
        ...step,
        secondaryHeaderComponent: {
            component: __vue_component__$1,
            props: {
                oldDraftWaypointID,
            },
        },
    };
};
const buildSchedulingStepKeyVal = (routeName, draftWaypointID, restrictAfterCompletion = false, subSteps, additionalParams) => [
    routeName,
    addAssociatedIDs({
        associatedParam: SchedulingFlowParam.DRAFT_WAYPOINT_ID,
        routeName,
        subSteps,
        title: SchedulingFlowRouteTitles[routeName],
        ...restrictAfterCompletion && {
            getAllowNavigation: () => !getStore().getters['schedulingFlow/getLocallyFinalized'],
        },
    }, draftWaypointID, undefined, undefined, undefined, additionalParams),
];
// DRAFT WAYPOINT (ROOT) STEPS
const getAddressStepKeyVal = (draftWaypointID) => buildSchedulingStepKeyVal(SchedulingFlowRouteName.SCHEDULING_FLOW_ADDRESS, draftWaypointID, true, undefined, 
// Strip the visit type param if the user revists the address step. If this is
// not done, the visit type param will remain in the route object despite not
// displaying in the URL, and initialization of the time step will be broken.
{ [SchedulingFlowParam.VISIT_TYPE]: '' });
const getTimeStepKeyVal = (draftWaypointID) => buildSchedulingStepKeyVal(SchedulingFlowRouteName.SCHEDULING_FLOW_TIME, draftWaypointID, true);
const getVisitOverviewStepKeyVal = (draftWaypointID) => {
    const store = getStore();
    const stepKeyVal = buildSchedulingStepKeyVal(SchedulingFlowRouteName.SCHEDULING_FLOW_VISIT_OVERVIEW, draftWaypointID, true);
    const { isRescheduling } = store.state.schedulingFlow.localState;
    const { isNurseRescheduling } = store.state.schedulingFlow;
    if (!isRescheduling || isNurseRescheduling)
        return stepKeyVal;
    /** During patient rescheduling, we offer the ability to discard changes */
    const [routeName, step] = stepKeyVal;
    const stepWithDiscardChangesButton = addDiscardChangesSecondaryHeaderComponent(step);
    return [routeName, stepWithDiscardChangesButton];
};
const getReviewStepKeyVal = (draftWaypointID) => buildSchedulingStepKeyVal(SchedulingFlowRouteName.SCHEDULING_FLOW_REVIEW, draftWaypointID, true);
const getCompleteProfileStepKeyVal = (draftWaypointID) => buildSchedulingStepKeyVal(SchedulingFlowRouteName.SCHEDULING_FLOW_COMPLETE_PROFILE, draftWaypointID);
const getPracticeInfoStepKeyVal = (draftWaypointID) => buildSchedulingStepKeyVal(SchedulingFlowRouteName.SCHEDULING_FLOW_PRACTICE_INFO, draftWaypointID);
const getWalkInDateStepKeyVal = (draftWaypointID) => buildSchedulingStepKeyVal(SchedulingFlowRouteName.SCHEDULING_FLOW_WALK_IN_DATE, draftWaypointID);
const getFacilityTimeStepKeyVal = (draftWaypointID) => buildSchedulingStepKeyVal(SchedulingFlowRouteName.SCHEDULING_FLOW_FACILITY_TIME, draftWaypointID);
// STEP MAP BUILDING HELPERS
const getShouldIncludeWalkInDateStep = () => {
    const store = getStore();
    const { isBrickAndMortarVisit } = store.state.schedulingFlow.localState;
    if (!isPatientUser())
        return isBrickAndMortarVisit;
    return (isBrickAndMortarVisit
        && !store.getters['schedulingFlow/getIsAppointmentOnlyVariant']);
};
const getShouldScheduleFacilityTimeStep = () => {
    const store = getStore();
    const { selectedDrawPlaceID } = store.state.schedulingFlow;
    if (!selectedDrawPlaceID)
        return false;
    const useWalkInDateStep = getShouldIncludeWalkInDateStep();
    const isIntegrationLab = store.getters['schedulingFlow/getLabHasAnyIntegration'](selectedDrawPlaceID);
    return useWalkInDateStep && isIntegrationLab;
};
const getReschedulingJobStepKeyVal = (draftWaypointID) => buildSchedulingStepKeyVal(SchedulingFlowRouteName.SCHEDULING_FLOW_RESCHEDULE_LOADING, draftWaypointID);
// ROOT STEP MAP
const getSchedulingStepMap = (draftWaypointID) => {
    var _a;
    const store = getStore();
    // The step building logic may throw errors in case of missing data. Return
    // empty steps when data is not ready.
    if (!draftWaypointID || !((_a = flowConfig.getFlowDataIsReady) === null || _a === void 0 ? void 0 : _a.call(flowConfig, draftWaypointID))) {
        return new Map();
    }
    const { isNurseRescheduling, isPatientInitiated, localState, } = store.state.schedulingFlow;
    const visitFinalized = localState.locallyFinalized;
    const stepMap = new Map();
    // After finalization, earlier steps are removed
    if (!visitFinalized) {
        stepMap.set(...getAddressStepKeyVal(draftWaypointID));
        /** Disabled the flexible scheduling flow for now */
        stepMap.set(...getTimeStepKeyVal(draftWaypointID));
        const timeStepKeyVal = getTimeStepKeyVal(draftWaypointID);
        const [routeName, step] = timeStepKeyVal;
        const updatedStep = addTimeHeaderComponents(step);
        const timeStep = [routeName, updatedStep];
        stepMap.set(...timeStep);
        if (getShouldScheduleFacilityTimeStep()) {
            const [facilityRouteName, facilityStep] = getFacilityTimeStepKeyVal(draftWaypointID);
            stepMap.set(facilityRouteName, addTimeHeaderComponents(facilityStep));
        }
        else if (getShouldIncludeWalkInDateStep()) {
            stepMap.set(...getWalkInDateStepKeyVal(draftWaypointID));
        }
        stepMap.set(...getVisitOverviewStepKeyVal(draftWaypointID));
    }
    if (isNurseRescheduling) {
        stepMap.set(...getReschedulingJobStepKeyVal(draftWaypointID));
    }
    else {
        stepMap.set(...getReviewStepKeyVal(draftWaypointID));
    }
    if (isPatientInitiated) {
        stepMap.set(...getCompleteProfileStepKeyVal(draftWaypointID));
        const { draftOrder } = store.state.orderFlow;
        /** Only request doctor info if office is a generic office */
        if ((draftOrder === null || draftOrder === void 0 ? void 0 : draftOrder.officeID) === GenericOfficeIDs.PATIENT_INITIATED) {
            stepMap.set(...getPracticeInfoStepKeyVal(draftWaypointID));
        }
    }
    return stepMap;
};
// HELPERS
const getSchedulingFlowHelpers = (route) => {
    const stepMap = getSchedulingStepMap(route.params.draftWaypointID);
    return initFlowHelpers(flowConfig, stepMap);
};
// ROUTER HELPERS
/** Dynamically get the first step in the order flow */
const getSchedulingFlowFirstStepRoute = (draftWaypointID, oldDraftWaypointID, isRescheduling, location, shiftID, skipAddressStep = false) => {
    var _a;
    if (!((_a = flowConfig.getFlowDataIsReady) === null || _a === void 0 ? void 0 : _a.call(flowConfig, draftWaypointID)))
        return null;
    const stepMap = getSchedulingStepMap(draftWaypointID);
    const { getFlattenedSteps, getRouteFromStep, } = initFlowHelpers(flowConfig, stepMap);
    const flatSteps = getFlattenedSteps();
    /** During a nurse reschedule, we skip the address step */
    const firstStep = skipAddressStep ? flatSteps[1] : flatSteps[0];
    if (!firstStep)
        throw toastError('Missing first step in scheduling flow.');
    const route = getRoute();
    const firstStepRoute = getRouteFromStep(firstStep, draftWaypointID);
    /** Retain current route params to retain partner route if provided on patient app */
    return {
        ...firstStepRoute,
        params: {
            ...route.value.params,
            ...firstStepRoute.params,
        },
        query: {
            ...(isRescheduling
                ? {
                    ...(oldDraftWaypointID && { from: oldDraftWaypointID }),
                    ...(location ? { location: JSON.stringify(location) } : {}),
                    ...(shiftID && { shiftID }),
                }
                : {}),
        },
    };
};
/**
 * When entering the flow, the user should go to the first step.
 */
const getSchedulingFlowEntryRoute = (draftWaypointID, oldDraftWaypointID, isRescheduling, location, shiftID, skipAddressStep = false) => getSchedulingFlowFirstStepRoute(draftWaypointID, oldDraftWaypointID, isRescheduling, location, shiftID, skipAddressStep);

export { getSchedulingFlowEntryRoute, getSchedulingFlowFirstStepRoute, getSchedulingFlowHelpers };
